/* eslint-disable no-undef, camelcase, radix, no-underscore-dangle */
export default class SavingsTable {
  // ============================================
  // CONFIGURATION - Centralized parameters
  // ============================================

  constructor(el) {
    const { SELECTORS, DATA_ATTRS, DEFAULTS } =
      SavingsTable.CONFIG;

    this.$el = $(el);
    this.$content = this.$el.find(SELECTORS.CONTENT);
    this.$noResults = this.$el.find(SELECTORS.NO_RESULTS);
    this.$resultsCount = this.$el.find(SELECTORS.RESULTS_COUNT);
    this.$rows = this.$el.find(SELECTORS.ROW); 
    this.$sortSelect = this.$el.find(SELECTORS.SORT_SELECT);

    // Calculator elements
    this.$calculator = this.$el.find(SELECTORS.CALCULATOR);
    this.$calculatorForm = this.$el.find(SELECTORS.CALCULATOR_FORM);
    this.$calculatorReset = this.$el.find(SELECTORS.CALCULATOR_RESET);

    // Filter elements
    this.$clearFiltersBtn = this.$el.find(SELECTORS.CLEAR_FILTERS_BTN);
    this.$filterAccountType = this.$el.find(SELECTORS.FILTER_ACCOUNT_TYPE);
    this.$filterRateType = this.$el.find(SELECTORS.FILTER_RATE_TYPE);
    this.$filterWithdrawals = this.$el.find(SELECTORS.FILTER_WITHDRAWALS);
    this.$filterMemberOnly = this.$el.find(SELECTORS.FILTER_MEMBER_ONLY);
    this.$calculatorToggle = this.$el.find(SELECTORS.CALCULATOR_TOGGLE);
    this.$loading = this.$el.find(SELECTORS.LOADING);

    // Data attributes
    this.isLoading = false;
    this.enableCalculator = this.$content.data(DATA_ATTRS.ENABLE_CALCULATOR) === 1;
    this.devMode = this.$content.data(DATA_ATTRS.DEV_MODE);
    this.pageContext = this.$content.data(DATA_ATTRS.PAGE_CONTEXT) || null;
    this._debounceTimer = null;

    // Initialize state from URL params (cache URLSearchParams)
    const urlParams = new URLSearchParams(window.location.search);
    this.currentSort = urlParams.get(SavingsTable.CONFIG.URL_PARAMS.SORT_BY) || DEFAULTS.SORT;
    this.calculatorFilters = this.getCalculatorFiltersFromURL(urlParams);
    this.hasActiveCalculatorFilters = this.calculatorFilters !== null;
    this.dropdownFilters = this.getFiltersFromURL(urlParams);
    this.hasActiveDropdownFilters = Object.values(this.dropdownFilters).some(
      (val) => val !== '',
    );
  }

  init() {
    this.bindEvents();
    if (this.enableCalculator) {
      this.initCalculator();
    }
    
    // Initialize row calculators for existing rows on page load
    this.initRowCalculators();
    
    // Bind calculator events for savings account rows
    this.bindRowCalculatorEvents();
  }

  bindEvents() {
    const { EVENTS } = SavingsTable.CONFIG;

    // Use namespaced events for cleanup
    const namespace = `.savingsTable${Date.now()}`;
    this._eventNamespace = namespace;

    $(document).on(`${EVENTS.SORT_CHANGED}${namespace}`, (_e, value, _label) => {
      this.currentSort = value;
      this.applyAllFilters();
    });

    $(document).on(`${EVENTS.FILTER_CHANGED}${namespace}`, (_e, _value, _label) => {
      this.collectDropdownFilters();
      this.applyAllFilters();
    });

    this.$clearFiltersBtn.on('click', () => {
      this.clearAllFilters();
    });

    this.$calculatorToggle.on('click', () => {
      this.toggleCalculator();
    });
  }

  // Cleanup method for destroying instance
  destroy() {
    if (this._eventNamespace) {
      $(document).off(this._eventNamespace);
    }
    this.$clearFiltersBtn.off('click');
    this.$calculatorToggle.off('click');
    if (this.$calculatorForm.length) {
      this.$calculatorForm.off('submit');
      this.$calculatorReset.off('click');
    }
    clearTimeout(this._debounceTimer);
  }

  toggleCalculator() {
    const { CLASSES } = SavingsTable.CONFIG;
    this.$calculatorToggle.toggleClass(CLASSES.ACTIVE);
    this.$calculator.stop().slideToggle();
  }

  initCalculator() {
    // Bind calculator form events
    this.$calculatorForm.on('submit', (e) => {
      e.preventDefault();
      this.handleCalculatorSubmit();
    });

    this.$calculatorReset.on('click', (e) => {
      e.preventDefault();
      this.handleCalculatorReset();
    });

    // Initialize sliders with bi-directional sync
    this.initSliders();

    // Populate form with values from URL if calculator filters exist
    if (this.calculatorFilters) {
      this.populateCalculatorForm(this.calculatorFilters);
      
      // Show calculator if it's hidden
      const { CLASSES } = SavingsTable.CONFIG;
      if (!this.$calculator.is(':visible')) {
        this.$calculatorToggle.addClass(CLASSES.ACTIVE);
        this.$calculator.show();
      }
      
      // Emit event to trigger calculator results on page load
      setTimeout(() => {
        $(document).trigger('savingsCalculator:submit', [this.calculatorFilters]);
      }, 100);
    }
  }

  // Helper: Update slider fill percentage
  updateSliderFill($slider, value) {
    const { SLIDER_PERCENT_VAR } = SavingsTable.CONFIG;
    const min = parseFloat($slider.attr('min'));
    const max = parseFloat($slider.attr('max'));
    const percent = ((value - min) / (max - min)) * 100;
    $slider[0].style.setProperty(SLIDER_PERCENT_VAR, `${percent}%`);
  }

  // Populate calculator form with filter values
  populateCalculatorForm(filters) {
    const { FORM_FIELDS } = SavingsTable.CONFIG;

    // Set radio buttons
    this.$calculatorForm
      .find(`[name="${FORM_FIELDS.CUSTOMER_ELIGIBILITY}"][value="${filters.customer_eligibility}"]`)
      .prop('checked', true);
    this.$calculatorForm
      .find(`[name="${FORM_FIELDS.TERM_DURATION}"][value="${filters.term_duration}"]`)
      .prop('checked', true);

    // Set starting balance
    const $startingInput = this.$calculatorForm.find(`[name="${FORM_FIELDS.STARTING_BALANCE}"]`);
    const $startingSlider = this.$calculatorForm.find(`[name="${FORM_FIELDS.STARTING_BALANCE_SLIDER}"]`);
    $startingInput.val(filters.starting_balance);
    $startingSlider.val(filters.starting_balance);
    this.updateSliderFill($startingSlider, filters.starting_balance);

    // Set monthly savings
    const $monthlyInput = this.$calculatorForm.find(`[name="${FORM_FIELDS.MONTHLY_SAVINGS}"]`);
    const $monthlySlider = this.$calculatorForm.find(`[name="${FORM_FIELDS.MONTHLY_SAVINGS_SLIDER}"]`);
    $monthlyInput.val(filters.monthly_savings);
    $monthlySlider.val(filters.monthly_savings);
    this.updateSliderFill($monthlySlider, filters.monthly_savings);

    this.updateResetButtonState();
  }

  // Helper: Calculate 12-month compound interest with monthly contributions and tier support
  calculate12MonthInterest(startingBalance, monthlySavings, applicableRate) {
    let balance = startingBalance;

    // Calculate compound interest for 12 months with monthly contributions
    for (let i = 0; i < 12; i++) {
      balance += monthlySavings;  // góp đầu tháng

      // Apply interest with fixed rate for all months
      const r = applicableRate / 100;  // Convert % to decimal
      const rm = Math.pow(1 + r, 1/12) - 1;  // Monthly rate
      balance *= (1 + rm);  // tính lãi tháng đó
    }

    const totalDeposit = startingBalance + (12 * monthlySavings);
    const estimatedInterest = balance - totalDeposit;

    return {
      estimatedInterest: estimatedInterest,
      finalBalance: balance,
      totalDeposit: totalDeposit
    };
  }

  // Initialize calculators for savings account rows
  initRowCalculators() {
    // Import and initialize SavingsAccountRowCalculator for each row
    this.$content.find('.savings-account-row').each((_index, el) => {
      const $el = $(el);

      // Skip if already initialized
      if ($el.data('calculator-initialized')) {
        return;
      }

      // Capture SavingsTable instance for use in calculator methods
      const savingsTable = this;

      // Initialize calculator (using same pattern as the standalone script)
      const calculator = {
        $el: $el,
        $result: $el.find('.savings-account-row__result'),
        $calculatorAmount: $el.find('.savings-account-row__calculator-amount'),
        $calculatorInterest: $el.find('.savings-account-row__calculator-interest'),
        $calculatorRate: $el.find('.savings-account-row__calculator-rate'),
        
        tier1Min: parseFloat($el.data('tier1-min')) || 0,
        tier1Rate: parseFloat($el.data('tier1-rate')) || 0,
        tier2Min: null,
        tier2Rate: null,
        isTwoTier: false,
        
        init() {
          const tier2MinData = this.$el.data('tier2-min');
          const tier2RateData = this.$el.data('tier2-rate');
          
          this.tier2Min = tier2MinData !== undefined && tier2MinData !== '' && tier2MinData !== null ? parseFloat(tier2MinData) : null;
          this.tier2Rate = tier2RateData !== undefined && tier2RateData !== '' && tier2RateData !== null ? parseFloat(tier2RateData) : null;
          
          const tier1Valid = this.tier1Min > 0 && this.tier1Rate > 0;
          const tier2Valid = this.tier2Min !== null && this.tier2Rate !== null && !isNaN(this.tier2Min) && !isNaN(this.tier2Rate);
          this.isTwoTier = tier1Valid && tier2Valid;
        },
        
        calculate(startingBalance, monthlySavings = 0) {
          if (startingBalance <= 0 || startingBalance < this.tier1Min) {
            this.$result.addClass('hidden');
            return;
          }

          const { rate: applicableRate, tier } = this.getApplicableRateWithTier(startingBalance);

          // Use shared calculation helper with tier support
          const result = savingsTable.calculate12MonthInterest(
            startingBalance,
            monthlySavings,
            applicableRate
          );

          this.$calculatorAmount.text(this.formatCurrency(startingBalance));
          this.$calculatorInterest.text(this.formatCurrency(result.estimatedInterest));
          this.$calculatorRate.text(`${applicableRate.toFixed(2)}%`);
          this.$result.removeClass('hidden');
        },
        
        getApplicableRate(amount) {
          if (!this.isTwoTier) {
            const tier2Valid = this.tier2Min !== null && this.tier2Rate !== null && !isNaN(this.tier2Min) && !isNaN(this.tier2Rate);
            return tier2Valid ? this.tier2Rate : this.tier1Rate;
          }
          
          if (amount >= this.tier2Min) {
            return this.tier2Rate;
          } else if (amount >= this.tier1Min) {
            return this.tier1Rate;
          }
          return this.tier1Rate;
        },
        
        getApplicableRateWithTier(_amount) {
          const tier2Valid = this.tier2Rate !== null && !isNaN(this.tier2Rate);
          const usesTier2 = tier2Valid && this.tier2Rate > this.tier1Rate;
          const rate = usesTier2 ? this.tier2Rate : this.tier1Rate;
          const tierLabel = usesTier2 ? 'Tier 2' : 'Tier 1';
          return { rate, tier: tierLabel };
        },
        
        formatCurrency(amount) {
          return `\u00A3${amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },
        
        reset() {
          this.$result.addClass('hidden');
        }
      };
      
      calculator.init();
      $el.data('calculator', calculator);
      $el.data('calculator-initialized', true);
    });
  }

  // Bind calculator events for savings account rows
  bindRowCalculatorEvents() {
    // Listen for calculator submit event
    $(document).on('savingsCalculator:submit', (_e, data) => {
      const startingBalance = data.starting_balance || 0;
      const monthlySavings = data.monthly_savings || 0;

      this.$content.find('.savings-account-row').each((_index, el) => {
        const $el = $(el);
        const calculator = $el.data('calculator');

        if (calculator) {
          calculator.calculate(startingBalance, monthlySavings);
        } else {
          // Fallback: create inline calculator if not initialized by SavingsTable
          const tier1Min = parseFloat($el.data('tier1-min')) || 0;
          const tier1Rate = parseFloat($el.data('tier1-rate')) || 0;
          
          if (startingBalance < tier1Min) {
            return;
          }
          
          const tier2MinData = $el.data('tier2-min');
          const tier2RateData = $el.data('tier2-rate');
          const tier2Min = tier2MinData !== undefined && tier2MinData !== '' && tier2MinData !== null ? parseFloat(tier2MinData) : null;
          const tier2Rate = tier2RateData !== undefined && tier2RateData !== '' && tier2RateData !== null ? parseFloat(tier2RateData) : null;
          
          const tier1Valid = tier1Min > 0 && tier1Rate > 0;
          const tier2Valid = tier2Min !== null && tier2Rate !== null && !isNaN(tier2Min) && !isNaN(tier2Rate);
          const isTwoTier = tier1Valid && tier2Valid;
          
          const usesTier2 = tier2Valid && tier2Rate > tier1Rate;
          const applicableRate = usesTier2 ? tier2Rate : tier1Rate;
          const tierLabel = usesTier2 ? 'Tier 2' : 'Tier 1';

          // Use shared calculation helper with tier support
          const result = this.calculate12MonthInterest(
            startingBalance,
            monthlySavings,
            applicableRate
          );

          const formatCurrency = (amount) => `\u00A3${amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

          $el.find('.savings-account-row__calculator-amount').text(formatCurrency(startingBalance));
          $el.find('.savings-account-row__calculator-interest').text(formatCurrency(result.estimatedInterest));
          $el.find('.savings-account-row__calculator-rate').text(`${applicableRate.toFixed(2)}%`);
          $el.find('.savings-account-row__result').removeClass('hidden');
        }
      });
    });

    // Listen for calculator reset event
    $(document).on('savingsCalculator:reset', () => {
      this.$content.find('.savings-account-row').each((_index, el) => {
        const $el = $(el);
        const calculator = $el.data('calculator');
        
        if (calculator) {
          calculator.reset();
        } else {
          $el.find('.savings-account-row__result').addClass('hidden');
        }
      });
    });
  }

  // Helper: Setup bi-directional sync for a slider-input pair
  setupSliderSync($slider, $input) {
    // Sync slider -> input
    $slider.on('input', (e) => {
      const value = parseInt(e.target.value);
      $input.val(value);
      this.updateSliderFill($slider, value);
    });

    // Sync input -> slider
    $input.on('input', (e) => {
      let value = parseInt(e.target.value) || 0;
      const min = parseInt($slider.attr('min'));
      const max = parseInt($slider.attr('max'));
      value = Math.max(min, Math.min(max, value));
      $input.val(value);
      $slider.val(value);
      this.updateSliderFill($slider, value);
    });

    // Initialize slider fill
    this.updateSliderFill($slider, parseFloat($slider.val()));
  }

  initSliders() {
    const { FORM_FIELDS } = SavingsTable.CONFIG;

    const $startingSlider = this.$calculatorForm.find(`[name="${FORM_FIELDS.STARTING_BALANCE_SLIDER}"]`);
    const $startingInput = this.$calculatorForm.find(`[name="${FORM_FIELDS.STARTING_BALANCE}"]`);
    const $startingError = this.$calculatorForm.find('[data-error-for="starting_balance"]');
    this.setupSliderSync($startingSlider, $startingInput);

    const $monthlySlider = this.$calculatorForm.find(`[name="${FORM_FIELDS.MONTHLY_SAVINGS_SLIDER}"]`);
    const $monthlyInput = this.$calculatorForm.find(`[name="${FORM_FIELDS.MONTHLY_SAVINGS}"]`);
    const $monthlyError = this.$calculatorForm.find('[data-error-for="monthly_savings"]');
    this.setupSliderSync($monthlySlider, $monthlyInput);
    
    // Remove error class and hide error message when user types/changes values
    $startingInput.on('input change', () => {
      if (parseInt($startingInput.val()) > 0) {
        $startingInput.removeClass('error');
        $startingError.addClass('hidden');
      }
    });
    
    $monthlyInput.on('input change', () => {
      if (parseInt($monthlyInput.val()) > 0) {
        $monthlyInput.removeClass('error');
        $monthlyError.addClass('hidden');
      }
    });
  }

  handleCalculatorSubmit() {
    const { FORM_FIELDS, DEFAULTS } = SavingsTable.CONFIG;

    // Get input values
    const $startingInput = this.$calculatorForm.find(`[name="${FORM_FIELDS.STARTING_BALANCE}"]`);
    const $monthlyInput = this.$calculatorForm.find(`[name="${FORM_FIELDS.MONTHLY_SAVINGS}"]`);
    const $startingError = this.$calculatorForm.find('[data-error-for="starting_balance"]');
    const $monthlyError = this.$calculatorForm.find('[data-error-for="monthly_savings"]');
    
    const startingBalance = parseInt($startingInput.val()) || 0;
    const monthlySavings = parseInt($monthlyInput.val()) || 0;

    // Validate inputs
    let hasError = false;
    
    if (startingBalance <= 0) {
      $startingInput.addClass('error');
      $startingError.removeClass('hidden');
      hasError = true;
    } else {
      $startingInput.removeClass('error');
      $startingError.addClass('hidden');
    }
    
    if (monthlySavings <= 0) {
      $monthlyInput.addClass('error');
      $monthlyError.removeClass('hidden');
      hasError = true;
    } else {
      $monthlyInput.removeClass('error');
      $monthlyError.addClass('hidden');
    }
    
    // If validation fails, don't submit
    if (hasError) {
      return;
    }

    // Collect calculator filter values
    this.calculatorFilters = {
      customer_eligibility: this.$calculatorForm
        .find(`[name="${FORM_FIELDS.CUSTOMER_ELIGIBILITY}"]:checked`)
        .val(),
      term_duration: this.$calculatorForm
        .find(`[name="${FORM_FIELDS.TERM_DURATION}"]:checked`)
        .val(),
      starting_balance: startingBalance,
      monthly_savings: monthlySavings,
    };

    // Mark calculator filters as active
    this.hasActiveCalculatorFilters = true;
    this.updateResetButtonState();

    // Emit event for savings-account-row calculators
    $(document).trigger('savingsCalculator:submit', [this.calculatorFilters]);

    // Apply all filters (calculator + dropdown + sort)
    this.applyAllFilters();
  }

  pushSavingsCalcSuccessEvent() {
    if (!this.calculatorFilters) {
      return;
    }

    // Count visible result boxes (not hidden)
    const visibleResults = this.$content.find('.savings-account-row__result:not(.hidden)').length;

    // Only push if there's at least one visible result
    if (visibleResults === 0) {
      return;
    }

    // Push to dataLayer
    window.dataLayer = window.dataLayer || [];
    /* eslint-disable camelcase */
    window.dataLayer.push({
      event: 'savings_calc_success',
      calc_starting_balance: this.calculatorFilters.starting_balance,
      calc_monthly_savings: this.calculatorFilters.monthly_savings,
      calc_customer_eligibility: this.calculatorFilters.customer_eligibility,
      calc_term_duration: this.calculatorFilters.term_duration,
      calc_products_count: visibleResults,
      source_page_url: window.location.href,
    });
    /* eslint-enable camelcase */
  }

  handleCalculatorReset() {
    const { DEFAULTS } = SavingsTable.CONFIG;

    // Clear calculator filters
    this.calculatorFilters = null;
    this.hasActiveCalculatorFilters = false;

    // Hide results count immediately
    this.$resultsCount.hide();

    // Reset form to defaults using shared method
    this.populateCalculatorForm({
      customer_eligibility: DEFAULTS.CUSTOMER_ELIGIBILITY,
      term_duration: DEFAULTS.TERM_DURATION,
      starting_balance: DEFAULTS.STARTING_BALANCE,
      monthly_savings: DEFAULTS.MONTHLY_SAVINGS,
    });

    // Emit event for savings-account-row calculators
    $(document).trigger('savingsCalculator:reset');

    this.applyAllFilters();
  }

  // Update reset button enabled/disabled state (for calculator)
  updateResetButtonState() {
    this.$calculatorReset.prop('disabled', !this.hasActiveCalculatorFilters);
  }

  // Helper: Set or delete URL param based on value
  setOrDeleteParam(params, key, value) {
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
  }

  // Update URL with all params (calculator, dropdown filters, sort)
  updateAllURLParams() {
    const { URL_PARAMS, FILTER_KEYS, DEFAULTS } = SavingsTable.CONFIG;
    const url = new URL(window.location.href);
    const params = url.searchParams;

    // Calculator params
    if (this.calculatorFilters) {
      params.set(URL_PARAMS.CALC_ELIGIBILITY, this.calculatorFilters.customer_eligibility);
      params.set(URL_PARAMS.CALC_TERM, this.calculatorFilters.term_duration);
      params.set(URL_PARAMS.STARTING_BALANCE, this.calculatorFilters.starting_balance);
      params.set(URL_PARAMS.MONTHLY_CONTRIBUTION, this.calculatorFilters.monthly_savings);
    } else {
      params.delete(URL_PARAMS.CALC_ELIGIBILITY);
      params.delete(URL_PARAMS.CALC_TERM);
      params.delete(URL_PARAMS.STARTING_BALANCE);
      params.delete(URL_PARAMS.MONTHLY_CONTRIBUTION);
    }

    // Dropdown filter params
    this.setOrDeleteParam(params, URL_PARAMS.ACCOUNT_TYPE, this.dropdownFilters[FILTER_KEYS.ACCOUNT_TYPE]);
    this.setOrDeleteParam(params, URL_PARAMS.RATE_TYPE, this.dropdownFilters[FILTER_KEYS.RATE_TYPE]);
    this.setOrDeleteParam(params, URL_PARAMS.WITHDRAWALS_ALLOWED, this.dropdownFilters[FILTER_KEYS.WITHDRAWALS_ALLOWED]);
    this.setOrDeleteParam(params, URL_PARAMS.MEMBER_ONLY, this.dropdownFilters[FILTER_KEYS.MEMBER_ONLY]);

    // Sort param (skip default)
    this.setOrDeleteParam(params, URL_PARAMS.SORT_BY, this.currentSort !== DEFAULTS.SORT ? this.currentSort : '');

    window.history.pushState({}, '', url.toString());
  }

  // Get dropdown filter values from URL params
  getFiltersFromURL(urlParams = null) {
    const { URL_PARAMS, FILTER_KEYS } = SavingsTable.CONFIG;
    const params = urlParams || new URLSearchParams(window.location.search);

    return {
      [FILTER_KEYS.ACCOUNT_TYPE]: params.get(URL_PARAMS.ACCOUNT_TYPE) || '',
      [FILTER_KEYS.RATE_TYPE]: params.get(URL_PARAMS.RATE_TYPE) || '',
      [FILTER_KEYS.WITHDRAWALS_ALLOWED]: params.get(URL_PARAMS.WITHDRAWALS_ALLOWED) || '',
      [FILTER_KEYS.MEMBER_ONLY]: params.get(URL_PARAMS.MEMBER_ONLY) || '',
    };
  }

  // Get calculator filter values from URL params
  getCalculatorFiltersFromURL(urlParams = null) {
    const { URL_PARAMS, DEFAULTS } = SavingsTable.CONFIG;
    const params = urlParams || new URLSearchParams(window.location.search);

    const eligibility = params.get(URL_PARAMS.CALC_ELIGIBILITY);
    const term = params.get(URL_PARAMS.CALC_TERM);
    const balance = params.get(URL_PARAMS.STARTING_BALANCE);
    const monthly = params.get(URL_PARAMS.MONTHLY_CONTRIBUTION);

    if (!eligibility && !term && !balance && !monthly) {
      return null;
    }

    return {
      customer_eligibility: eligibility || DEFAULTS.CUSTOMER_ELIGIBILITY,
      term_duration: term || DEFAULTS.TERM_DURATION,
      starting_balance: balance ? parseInt(balance) : DEFAULTS.STARTING_BALANCE,
      monthly_savings: monthly ? parseInt(monthly) : DEFAULTS.MONTHLY_SAVINGS,
    };
  }

  // Collect current dropdown filter values from UI
  collectDropdownFilters() {
    const { SELECTORS, DATA_ATTRS, FILTER_KEYS } = SavingsTable.CONFIG;

    this.dropdownFilters = {
      [FILTER_KEYS.ACCOUNT_TYPE]:
        this.$filterAccountType
          .find(SELECTORS.ACTIVE_OPTION)
          .data(DATA_ATTRS.VALUE) || '',
      [FILTER_KEYS.RATE_TYPE]:
        this.$filterRateType
          .find(SELECTORS.ACTIVE_OPTION)
          .data(DATA_ATTRS.VALUE) || '',
      [FILTER_KEYS.WITHDRAWALS_ALLOWED]:
        this.$filterWithdrawals
          .find(SELECTORS.ACTIVE_OPTION)
          .data(DATA_ATTRS.VALUE) || '',
      [FILTER_KEYS.MEMBER_ONLY]:
        this.$filterMemberOnly
          .find(SELECTORS.ACTIVE_OPTION)
          .data(DATA_ATTRS.VALUE) || '',
    };

    this.hasActiveDropdownFilters = Object.values(this.dropdownFilters).some(
      (val) => val !== '',
    );
  }

  // Apply all filters (calculator + dropdown + sort) and reload data
  applyAllFilters() {
    this.updateClearFiltersButtonState();
    this.updateAllURLParams();

    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this.loadAccounts(), 300);
  }

  // Update results count display
  updateResultsCount(total) {
    if (!this.$resultsCount.length) {
      return;
    }

    // Only show results count if calculator filters are active AND both inputs are filled
    if (this.calculatorFilters && total > 0) {
      const hasStartingBalance = this.calculatorFilters.starting_balance > 0;
      const hasMonthlySavings = this.calculatorFilters.monthly_savings > 0;
      
      // Both inputs must be filled to show results
      if (hasStartingBalance && hasMonthlySavings) {
        const resultText = total === 1 ? 'Result' : 'Results';
        this.$resultsCount.find('p.h4, .h4').html(`Showing ${total} ${resultText}`);
        this.$resultsCount.show();
      } else {
        this.$resultsCount.hide();
      }
    } else {
      this.$resultsCount.hide();
    }
  }

  // Helper: Reset a dropdown if it exists
  resetDropdown($dropdown) {
    if ($dropdown.length && $dropdown.data('ggSelect')) {
      $dropdown.data('ggSelect').reset();
    }
  }

  // Clear all dropdown filters
  clearAllFilters() {
    const { FILTER_KEYS, DEFAULTS } = SavingsTable.CONFIG;

    // Reset all dropdowns UI
    this.resetDropdown(this.$filterAccountType);
    this.resetDropdown(this.$filterRateType);
    this.resetDropdown(this.$filterWithdrawals);
    this.resetDropdown(this.$filterMemberOnly);
    this.resetDropdown(this.$sortSelect);

    // Clear dropdown filters state
    this.dropdownFilters = {
      [FILTER_KEYS.ACCOUNT_TYPE]: '',
      [FILTER_KEYS.RATE_TYPE]: '',
      [FILTER_KEYS.WITHDRAWALS_ALLOWED]: '',
      [FILTER_KEYS.MEMBER_ONLY]: '',
    };
    this.hasActiveDropdownFilters = false;

    // Reset sort to default
    this.currentSort = DEFAULTS.SORT;

    this.applyAllFilters();
  }

  // Merge dropdown filters with page context fallback for account_type
  getFinalDropdownFilters() {
    const { FILTER_KEYS } = SavingsTable.CONFIG;
    const filters = { ...this.dropdownFilters };

    if (
      this.pageContext &&
      this.pageContext.term_slug &&
      !filters[FILTER_KEYS.ACCOUNT_TYPE]
    ) {
      filters[FILTER_KEYS.ACCOUNT_TYPE] = this.pageContext.term_slug;
    }

    return filters;
  }

  // Update clear filters button enabled/disabled state (for dropdown filters + sort)
  updateClearFiltersButtonState() {
    const { DEFAULTS } = SavingsTable.CONFIG;
    const hasNonDefaultSort = this.currentSort && this.currentSort !== DEFAULTS.SORT;
    const hasAnyFilters = this.hasActiveDropdownFilters || hasNonDefaultSort;
    this.$clearFiltersBtn.prop('disabled', !hasAnyFilters);
  }

  loadAccounts() {
    const { AJAX, CLASSES } = SavingsTable.CONFIG;

    if (this.isLoading) {
      return;
    }
    this.$loading.removeClass(CLASSES.HIDDEN);
    this.isLoading = true;

    const ajaxData = {
      action: AJAX.ACTION,
      posts_per_page: AJAX.POSTS_PER_PAGE,
      sort_by: this.currentSort,
      dev_mode: this.devMode,
    };

    if (this.calculatorFilters) {
      ajaxData.calculator_filters = this.calculatorFilters;
    }

    const finalDropdownFilters = this.getFinalDropdownFilters();
    const hasActiveDropdown = Object.values(finalDropdownFilters).some(
      (val) => val !== '',
    );
    if (hasActiveDropdown) {
      ajaxData.dropdown_filters = finalDropdownFilters;
    }

    $.ajax({
      type: 'POST',
      url: window.AJAX_URL,
      dataType: 'json',
      data: ajaxData,
      success: (response) => {
        if (response.success) {
          const hasResults = response.data.total > 0;

          if (hasResults) {
            // Show results, hide no results message
            this.$content.html(response.data.html);
            this.$content.show();
            this.$noResults.hide();
            
            // Update results count
            this.updateResultsCount(response.data.total);

            // Lazy load images if available
            if (typeof window.lazyLoadImage !== 'undefined') {
              window.lazyLoadImage.lazyLoadImage();
            }

            // Re-initialize calculators for newly loaded rows
            this.initRowCalculators();

            // Emit calculator event if calculator filters are active
            if (this.calculatorFilters) {
              setTimeout(() => {
                $(document).trigger('savingsCalculator:submit', [this.calculatorFilters]);
                
                // Push GTM dataLayer event after results are rendered
                setTimeout(() => {
                  this.pushSavingsCalcSuccessEvent();
                }, 150);
              }, 100);
            }
          } else {
            // No results found, show message
            this.$content.empty();
            this.$content.hide();
            this.$noResults.show();
            this.$resultsCount.hide();
          }
        } else {
          console.error('Failed to load accounts:', response.data?.message);
        }
        this.$loading.addClass(CLASSES.HIDDEN);
      },
      error: (_jqXHR, textStatus, errorThrown) => {
        console.error('AJAX error:', textStatus, errorThrown);
        this.$loading.addClass(CLASSES.HIDDEN);
      },
      complete: () => {
        this.isLoading = false;
        this.$loading.addClass(CLASSES.HIDDEN);
      },
    });
  }
}
SavingsTable.CONFIG = {
  // CSS Selectors
  SELECTORS: {
    CONTENT: '.gg-savings-table__content',
    NO_RESULTS: '.gg-savings-table__no-results',
    RESULTS_COUNT: '.gg-savings-table__results-count',
    ROW: '.gg-savings-table__row',
    SORT_SELECT: '.gg-savings-table__sort',
    CALCULATOR: '.gg-savings-table__calculator',
    CALCULATOR_FORM: '[data-calculator-form]',
    CALCULATOR_RESET: '.gg-savings-calculator__btn-reset',
    CALCULATOR_TOGGLE: '.js-toggle-calculator',
    CLEAR_FILTERS_BTN: '.gg-filter-controls__clear-btn',
    FILTER_ACCOUNT_TYPE: '.gg-savings-table__filter--account-type',
    FILTER_RATE_TYPE: '.gg-savings-table__filter--rate-type',
    FILTER_WITHDRAWALS: '.gg-savings-table__filter--withdrawals',
    FILTER_MEMBER_ONLY: '.gg-savings-table__filter--member-only',
    LOADING: '.gg-savings-table__loading',
    ACTIVE_OPTION: '.gg-select__option.active',
  },

  // Form field names
  FORM_FIELDS: {
    CUSTOMER_ELIGIBILITY: 'customer_eligibility',
    TERM_DURATION: 'term_duration',
    STARTING_BALANCE: 'starting_balance',
    STARTING_BALANCE_SLIDER: 'starting_balance_slider',
    MONTHLY_SAVINGS: 'monthly_savings',
    MONTHLY_SAVINGS_SLIDER: 'monthly_savings_slider',
  },

  // Default values
  DEFAULTS: {
    SORT: 'aer-high',
    CUSTOMER_ELIGIBILITY: 'both',
    TERM_DURATION: '0-1',
    STARTING_BALANCE: 0,
    MONTHLY_SAVINGS: 0,
  },

  // URL parameter names
  URL_PARAMS: {
    CALC_ELIGIBILITY: 'customer_eligibility',
    CALC_TERM: 'term_duration',
    STARTING_BALANCE: 'starting_balance',
    MONTHLY_CONTRIBUTION: 'monthly_savings',
    ACCOUNT_TYPE: 'account_type',
    RATE_TYPE: 'rate_type',
    WITHDRAWALS_ALLOWED: 'withdrawals_allowed',
    MEMBER_ONLY: 'member_only',
    SORT_BY: 'sort_by',
  },

  // Filter keys
  FILTER_KEYS: {
    ACCOUNT_TYPE: 'account_type',
    RATE_TYPE: 'rate_type',
    WITHDRAWALS_ALLOWED: 'withdrawals_allowed',
    MEMBER_ONLY: 'member_only',
  },

  // AJAX settings
  AJAX: {
    ACTION: 'load_savings_accounts',
    // Load all results
    POSTS_PER_PAGE: -1,
  },

  // Data attributes
  DATA_ATTRS: {
    ENABLE_CALCULATOR: 'enable-calculator',
    DEV_MODE: 'dev-mode',
    PAGE_CONTEXT: 'page-context',
    VALUE: 'value',
  },

  // Events
  EVENTS: {
    SORT_CHANGED: 'savingsTableSortChanged',
    FILTER_CHANGED: 'savingsTableFilterChanged',
  },

  // CSS classes
  CLASSES: {
    ACTIVE: 'active',
    HIDDEN: 'hidden',
  },

  // Slider CSS variable
  SLIDER_PERCENT_VAR: '--slider-percent',
};
// ============================================
// INITIALIZATION
// ============================================
const INIT_CONFIG = {
  BLOCK_SELECTOR: '.gg-savings-table',
  ACF_BLOCK_TYPE: 'render_block_preview/type=acf/gravity-savings-table',
};

// Initialize on page load
if (typeof window.acf === 'undefined') {
  $(document).ready(() => {
    $(INIT_CONFIG.BLOCK_SELECTOR).each((_index, el) => {
      new SavingsTable(el).init();
    });
  });
} else {
  acf.addAction(INIT_CONFIG.ACF_BLOCK_TYPE, ($el) => {
    $el.find(INIT_CONFIG.BLOCK_SELECTOR).each((_index, el) => {
      new SavingsTable(el).init();
    });
  });
}
